// Admin & Audit -- iteration 085

// SELF-consent policy types. The signed-in user consents to these about
// themselves. The ConsentGate + requireConsent middleware operate ONLY on this
// set -- do NOT add child_data here, or every authenticated user would be
// forced to self-consent to it (see require-consent.ts).
export const POLICY_TYPES = ["privacy", "terms", "families"] as const;
export type PolicyType = (typeof POLICY_TYPES)[number];

// child_data (143): a GUARDIAN consents to this on behalf of a minor player.
// Stored in the same policy_versions table but kept OUT of the self-consent set
// above. ALL_POLICY_TYPES is used only for policy publish/read validation.
export const CHILD_POLICY_TYPE = "child_data" as const;
export type ChildPolicyType = typeof CHILD_POLICY_TYPE;
export const ALL_POLICY_TYPES = [...POLICY_TYPES, CHILD_POLICY_TYPE] as const;
export type AnyPolicyType = (typeof ALL_POLICY_TYPES)[number];

export interface PolicyVersionPublic {
  policy_type: AnyPolicyType;
  version: string;
  body_markdown: string;
  effective_at: string;
  sunset_at: string | null;
}

export interface UserConsentPublic {
  id: string;
  user_id: string;
  policy_type: PolicyType;
  version: string;
  consented_at: string;
}

export interface UserConsentsResponse {
  consents: UserConsentPublic[];
  missing: PolicyType[]; // policy types with an active version the user has not consented to
}

// --- Child-data guardian consent (143) ---

export interface ChildConsentPublic {
  id: string;
  guardian_user_id: string;
  child_player_id: string;
  policy_type: ChildPolicyType;
  version: string;
  consented_at: string;
  withdrawn_at: string | null;
}

/** Per-child consent state for the guardian-facing surface. */
export interface ChildConsentStatus {
  player_id: string;
  first_name: string | null;
  surname: string | null;
  is_minor: boolean;
  consent_required: boolean; // is_minor AND an active child_data policy exists
  consented: boolean; // a current-version, non-withdrawn consent by this guardian exists
  current_version: string | null; // active child_data policy version, null if none published
  consented_version: string | null; // version this guardian last consented to (non-withdrawn)
}

export interface ChildrenConsentStatusResponse {
  policy: PolicyVersionPublic | null; // active child_data policy (body for the modal), null if none
  children: ChildConsentStatus[];
}

/** 403 body returned by the player surface when guardian consent is missing. */
export interface GuardianConsentRequired {
  error: "guardian_consent_required";
  reason: "no_active_policy" | "not_consented";
}

// 142 -- development-log tag taxonomy.
export const NOTE_TAGS = ["performance", "injury", "discipline", "attendance"] as const;
export type NoteTag = (typeof NOTE_TAGS)[number];

export interface PlayerCoachNotePublic {
  id: string;
  player_id: string;
  coach_id: string;
  coach_name: string; // 142 -- author, for the shared development log
  body: string;
  tag: NoteTag | null; // 142
  visibility: "private" | "school_coaches";
  created_at: string;
  updated_at: string;
}

export interface PlayerImportBatchPublic {
  id: string;
  school_id: string;
  imported_by: string;
  importer_display_name: string | null;
  row_count: number;
  created_count: number;
  updated_count: number;
  failed_count: number;
  created_at: string;
}
